import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineHeart, HiOutlineCloudArrowUp, HiOutlineDocumentText,
  HiOutlineCheckCircle, HiOutlineLockClosed, HiOutlineXMark,
  HiOutlineArrowRight, HiOutlineInformationCircle,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI, portalFileUrl } from "./portalApi";
import "./MedicalPage.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" }) : "";
const fmtSize = (b) => !b ? "" : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/(1024*1024)).toFixed(1)} MB`;

export default function MedicalPage() {
  const [loading,  setLoading]  = useState(true);
  const [status,   setStatus]   = useState(null);
  const [files,    setFiles]    = useState([]);
  const [uploading,setUploading]= useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");
  const inputRef = useRef(null);

  const fetchStatus = () => {
    portalAPI.getMedicalStatus()
      .then(res => setStatus(res?.data || null))
      .catch(() => setError("Could not load your medical clearance status."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const valid = [];
    for (const f of incoming) {
      if (f.size > 5 * 1024 * 1024) { setError(`${f.name} is over 5 MB — please choose a smaller file.`); continue; }
      const ext = f.name.split(".").pop().toLowerCase();
      if (!["pdf","jpg","jpeg","png"].includes(ext)) { setError(`${f.name} — only PDF, JPG, PNG files are allowed.`); continue; }
      valid.push(f);
    }
    if (valid.length) setError("");
    setFiles(prev => [...prev, ...valid].slice(0, 5));
  };

  const handleDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };
  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (files.length === 0) { setError("Please attach at least one document."); return; }
    setUploading(true); setError(""); setSuccess("");
    try {
      await portalAPI.submitMedical(files);
      setSuccess("Your medical documents have been submitted. Our team will review them shortly.");
      setFiles([]);
      fetchStatus();
    } catch (err) {
      setError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout eyebrow="Medical Clearance" pageTitle="Medical Clearance"
        quote="Taking care of your body is part of taking care of your future." quoteAuthor="Forge Retreat">
        <div className="med__loading"><div className="med__spinner"/><p>Loading…</p></div>
      </PortalLayout>
    );
  }

  /* ── Locked — screening not cleared yet ── */
  if (!status?.screeningCleared) {
    return (
      <PortalLayout eyebrow="Medical Clearance" pageTitle="Medical Clearance"
        quote="Taking care of your body is part of taking care of your future." quoteAuthor="Forge Retreat">
        <div className="med__locked-wrap">
          <div className="pc med__locked-card">
            <div className="med__locked-icon-ring"><HiOutlineLockClosed className="med__locked-icon"/></div>
            <h2 className="med__locked-title">Not Yet Available</h2>
            <p className="med__locked-sub">
              Medical document submission unlocks once your screening review is complete.
              We'll notify you as soon as you can move to this step.
            </p>
            <Link to="/portal/journey" className="p-btn p-btn--outline" style={{marginTop:"1rem",display:"inline-flex"}}>
              View Your Journey <HiOutlineArrowRight/>
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout eyebrow="Medical Clearance" pageTitle="Medical Clearance"
      quote="Taking care of your body is part of taking care of your future." quoteAuthor="Forge Retreat">

      {/* Status bar */}
      <div className="pc med__status-bar">
        <div className="med__status-left">
          <div className="p-icon-circle p-icon-circle--forest" style={{width:"3rem",height:"3rem"}}>
            <HiOutlineHeart/>
          </div>
          <div>
            <p className="med__status-eyebrow">Medical Clearance Status</p>
            {status.medicalCleared ? (
              <p className="med__status-value">Cleared <span className="med__check">✓</span></p>
            ) : status.medicalSubmitted ? (
              <p className="med__status-value med__status-value--pending">Under Review</p>
            ) : (
              <p className="med__status-value med__status-value--pending">Awaiting Your Submission</p>
            )}
            {status.medicalSubmittedAt && <p className="med__status-date">Submitted {fmt(status.medicalSubmittedAt)}</p>}
          </div>
        </div>
      </div>

      {!status.medicalCleared && (
        <div className="pc med__intro">
          <HiOutlineInformationCircle className="med__intro-icon"/>
          <div>
            <p className="med__intro-title">What we need from you</p>
            <p className="med__intro-sub">
              Please upload your medical clearance paperwork — this may include a doctor's note,
              a recent physical exam summary, or any relevant medical history that helps our team
              ensure your safety during retreat activities. You can upload up to 5 files.
            </p>
          </div>
        </div>
      )}

      {/* Previously submitted documents */}
      {status.documents?.length > 0 && (
        <div className="pc med__docs-card">
          <p className="p-section-title">Submitted Documents</p>
          <div className="med__doc-list">
            {status.documents.map(d => (
              <div key={d.id} className="med__doc-row">
                <div className="med__doc-icon"><HiOutlineDocumentText/></div>
                <div className="med__doc-info">
                  <p className="med__doc-name">{d.file_name}</p>
                  <p className="med__doc-meta">Uploaded {fmt(d.created_at)}</p>
                </div>
                <a href={portalFileUrl(`/portal/documents/${d.id}/download`)}
                  target="_blank" rel="noreferrer" className="p-btn p-btn--outline" style={{fontSize:"0.75rem"}}>
                  View
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload zone — only if not yet cleared */}
      {!status.medicalCleared && (
        <div className="pc med__upload-card">
          <p className="p-section-title">
            {status.medicalSubmitted ? "Submit Additional Documents" : "Upload Your Documents"}
          </p>

          <div
            className={`med__upload-zone ${files.length ? "med__upload-zone--has-files" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            role="button" tabIndex={0}
            onKeyDown={e => e.key === "Enter" && inputRef.current?.click()}
          >
            <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple
              style={{display:"none"}} onChange={e => handleFiles(e.target.files)}/>
            {files.length === 0 ? (
              <>
                <HiOutlineCloudArrowUp className="med__upload-icon"/>
                <p className="med__upload-text">Drop files here or click to browse</p>
                <p className="med__upload-hint">PDF, JPG, PNG — max 5 MB each, up to 5 files</p>
              </>
            ) : (
              <div className="med__file-list" onClick={e => e.stopPropagation()}>
                {files.map((f, i) => (
                  <div key={i} className="med__file-chip">
                    <HiOutlineDocumentText/>
                    <div className="med__file-chip-info">
                      <p className="med__file-chip-name">{f.name}</p>
                      <p className="med__file-chip-size">{fmtSize(f.size)}</p>
                    </div>
                    <button type="button" onClick={() => removeFile(i)}><HiOutlineXMark/></button>
                  </div>
                ))}
                {files.length < 5 && (
                  <button type="button" className="med__add-more" onClick={() => inputRef.current?.click()}>
                    + Add another file
                  </button>
                )}
              </div>
            )}
          </div>

          {error   && <p className="med__error">{error}</p>}
          {success && <p className="med__success"><HiOutlineCheckCircle/> {success}</p>}

          {files.length > 0 && (
            <div className="med__submit-row">
              <button type="button" className="p-btn p-btn--outline" onClick={() => setFiles([])} disabled={uploading}>
                Clear All
              </button>
              <button type="button" className="p-btn p-btn--fill" onClick={handleSubmit} disabled={uploading}>
                <HiOutlineCloudArrowUp/>
                {uploading ? "Uploading…" : `Submit ${files.length} Document${files.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          )}
        </div>
      )}

      {status.medicalCleared && (
        <div className="pc med__cleared-card">
          <HiOutlineCheckCircle className="med__cleared-icon"/>
          <div>
            <p className="med__cleared-title">You're all set!</p>
            <p className="med__cleared-sub">Your medical clearance has been reviewed and approved.</p>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
